"use client";

import { useEffect, useState } from "react";

import { usePrefersReducedMotion } from "@/hooks/use-prefers-reduced-motion";

// =============================================================================

/**
 * Plays a looping timeline while `active`: returns the index of the current
 * step. Each entry of `durations` is the time (ms) spent on that step.
 * Reset to the first step when inactive; reduced motion shows the last step.
 */
export function useSequence(active: boolean, durations: number[]): number {
  // Hydration-safe: the server always renders the first step
  const shouldReduceMotion = usePrefersReducedMotion();
  const [step, setStep] = useState(0);
  const lastStep = durations.length - 1;

  // Restart from the beginning every time the visual becomes active again
  const [wasActive, setWasActive] = useState(active);
  if (active !== wasActive) {
    setWasActive(active);
    if (active) setStep(0);
  }

  useEffect(() => {
    if (!active || shouldReduceMotion) return;
    const timeout = window.setTimeout(
      () => setStep((current) => (current >= lastStep ? 0 : current + 1)),
      durations[step] ?? 1000
    );
    return () => window.clearTimeout(timeout);
  }, [active, shouldReduceMotion, step, durations, lastStep]);

  return shouldReduceMotion ? lastStep : step;
}

// =============================================================================
